import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BsHeart, BsPeople, BsCollection } from "react-icons/bs";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { HiOutlineSparkles } from "react-icons/hi";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import { getChain } from "../../utils/currencies";

// Category display labels
const categoryLabels = {
  desci_general: "DeSci General",
  biotech_life_sciences: "Biotech & Life Sciences",
  ai_computer_science: "AI & Computer Science",
  open_source_software: "Open Source",
  women_in_science: "Women in Science",
  regional: "Regional",
  climate_environment: "Climate & Environment",
  space_astronomy: "Space & Astronomy",
  other: "Other",
};

// State colors
const stateColors = {
  draft: "bg-gray-500",
  pending_mint: "bg-yellow-500",
  deploying: "bg-blue-500",
  active: "bg-green-500",
  sold_out: "bg-red-500",
  paused: "bg-orange-500",
  closed: "bg-gray-700",
};

const CommunityCard = ({ data, isMyCommunity, isSlide }) => {
  const [chainIconFailed, setChainIconFailed] = useState(false);
  const {
    _id,
    name,
    shortDescription,
    artwork,
    category,
    membershipTier,
    totalSupply,
    soldCount,
    saleSettings,
    state,
    featured,
    likes,
    blockchain,
    creator,
    memberCount,
  } = data ?? {};

  const availableNFTs = totalSupply - (soldCount || 0);
  const chainInfo = getChain({ network: blockchain });
  const soldPercentage =
    totalSupply > 0 ? Math.round(((soldCount || 0) / totalSupply) * 100) : 0;

  return (
    <div
      className={`${
        isSlide ? "w-full h-full" : "w-full mlg:min-w-[350px] lg:min-w-[320px]"
      } flex flex-col rounded-3xl bg-white/95 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden font-roboto border border-white/20`}
    >
      {/* Community Image */}
      <Link
        to={`/communities/${_id}`}
        className={`w-full ${isSlide ? "h-48" : "h-64"} relative overflow-hidden group/card flex-shrink-0`}
      >
        <img
          src={artwork || "/placeholder-community.png"}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-110"
        />

        {/* Gradient Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300"></div>

        {/* State Badge */}
        <span
          className={`absolute left-0 top-4 rounded-r-xl text-xs font-semibold text-white px-3 py-1.5 capitalize shadow-lg ${stateColors[state]}`}
        >
          {state?.replace("_", " ")}
        </span>

        {/* Featured Badge */}
        {featured && (
          <span className="absolute right-3 top-4 flex items-center gap-1 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
            <HiOutlineSparkles className="text-sm" />
            Featured
          </span>
        )}

        {/* Chain Badge */}
        {chainInfo && (
          <div
            title={chainInfo.name}
            className="absolute right-3 bottom-3 bg-white/95 backdrop-blur-sm rounded-full p-2 shadow-lg border border-gray-200 flex items-center justify-center"
          >
            {chainInfo.icon && !chainIconFailed ? (
              <img
                src={chainInfo.icon}
                alt={chainInfo.name}
                className="w-6 h-6"
                onError={() => setChainIconFailed(true)}
              />
            ) : (
              <span className="text-xs font-bold text-gray-700 px-1">
                {chainInfo.shortName || chainInfo.name}
              </span>
            )}
          </div>
        )}

        {/* Edit Button for owner */}
        {isMyCommunity && (
          <div className="hidden group-hover/card:flex flex-col items-end gap-3 absolute right-3 top-14 z-10">
            <Link
              to={`/create-community?edit=${_id}`}
              className="flex items-center group/action"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="hidden group-hover/action:flex ml-2 text-white grow-up-animation bg-primary/90 backdrop-blur-sm py-2 pr-4 pl-3 rounded-lg text-sm shadow-lg font-medium">
                Edit
              </span>
              <div className="w-8 h-8 flex justify-center items-center rounded-full bg-primary shadow-lg z-10 hover:scale-110 transition-transform">
                <MdOutlineModeEditOutline color="#ffffff" className="text-lg" />
              </div>
            </Link>
          </div>
        )}
      </Link>

      {/* Community Details */}
      <div className="flex-1 flex flex-col p-5 justify-between">
        {/* Title */}
        <Link
          to={`/communities/${_id}`}
          className="font-bold text-lg text-gray-900 hover:text-primary transition-colors mb-3 line-clamp-2 min-h-[3.5rem]"
        >
          {name || "Unnamed Community"}
        </Link>

        {/* Info Rows */}
        <div className="flex flex-col gap-2.5 pb-4 border-b border-gray-200 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 font-medium">Category:</span>
            <span className="text-xs font-bold text-gray-800">
              {categoryLabels[category] || category || "General"}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 font-medium">Membership:</span>
            <span className="text-xs font-bold text-gray-800 capitalize">
              {membershipTier || "Standard"}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 font-medium">Payment:</span>
            <span className="text-xs font-bold text-primary">
              {(Number(saleSettings?.price) || 0) === 0
                ? "Free"
                : `${saleSettings.price} ${saleSettings?.currency || "ETH"}`}{" "}
              on {chainInfo?.name || blockchain || "Unknown"}
            </span>
          </div>
        </div>

        {/* Creator */}
        <div className="flex items-center gap-2 mb-4">
          <Link
            to={`/profile?id=${creator?._id}`}
            className="rounded-full overflow-hidden flex justify-center items-center cursor-pointer h-10 w-10 flex-shrink-0 ring-2 ring-gray-200 hover:ring-primary transition-all"
          >
            <img
              src={creator?.images?.profile || UserPlaceHolderImg}
              alt={creator?.fundername}
              className="transition-all duration-300 hover:scale-110 w-full h-full object-cover"
            />
          </Link>
          <div className="flex flex-col flex-1 min-w-0">
            <Link
              to={`/profile?id=${creator?._id}`}
              className="text-sm font-bold hover:text-primary transition-colors truncate"
            >
              {creator?.fundername ||
                `${creator?.firstname || ""} ${creator?.lastname || ""}`.trim() ||
                "Creator"}
            </Link>
            <span className="text-gray-500 text-xs truncate">
              {creator?.title_rank || "Community Creator"}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full flex flex-col">
          <div className="w-full relative rounded-full mb-3 bg-gray-200 h-2 overflow-hidden">
            <div
              style={{ width: `${Math.min(100, soldPercentage)}%` }}
              className="bg-gradient-to-r from-primary to-primary-green absolute left-0 top-0 h-full rounded-full transition-all duration-500 shadow-sm"
            />
          </div>
          <div className="w-full grid grid-cols-4 gap-2 poppins-font">
            <div className="flex flex-col items-center bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors">
              <span className="font-bold text-sm text-gray-900">{soldPercentage}%</span>
              <span className="text-[10px] text-gray-500">Sold</span>
            </div>
            <div className="flex flex-col items-center bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors">
              <span className="font-bold text-sm flex items-center gap-1 text-gray-900">
                <BsPeople className="text-xs text-primary" />
                {memberCount || 0}
              </span>
              <span className="text-[10px] text-gray-500">Members</span>
            </div>
            <div className="flex flex-col items-center bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors">
              <span className="font-bold text-sm flex items-center gap-1 text-gray-900">
                <BsCollection className="text-xs text-primary-green" />
                {availableNFTs}
              </span>
              <span className="text-[10px] text-gray-500">Available</span>
            </div>
            <div className="flex flex-col items-center bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors">
              <span className="font-bold text-sm flex items-center gap-1 text-gray-900">
                <BsHeart className="text-xs text-pink-500" />
                {likes || 0}
              </span>
              <span className="text-[10px] text-gray-500">Likes</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityCard;
